/* eslint-disable no-param-reassign */

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { PromiseContainer } from '../../../components';
import PulseKeyStatRequest, {
  PulseKeyStatRecord,
} from '../store/PulseKeyStatRequest';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

export const options: ChartOptions<any> = {
  maxBarThickness: 50,
  parsing: { xAxisKey: 'redeemedDate', yAxisKey: 'keyCount' },
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: true,
      text: 'Redemptions per day',
    },
  },
  responsive: true,

  // AI-generated fix for https://www.notion.so/pulseapp/b42749383d2f4eca8b6a65e05094fc05?v=e2f04fb569c543a99c59c078adb047e0&p=2941ed4c26a980939c1cefd6f399ca05&pm=s
  onResize(chart: { resizingNow: boolean } & ChartJS) {
    if (chart.resizingNow) {
      // prevent recursive resize
      return;
    }
    chart.resizingNow = true;
    const { resize } = chart;
    chart.resize = () => {};
    setTimeout(() => {
      chart.resize = resize.bind(chart);
      chart.resizingNow = false;
    }, 800);
  },
};

export default observer(function StatGraph({
  statRequest,
}: {
  statRequest: PulseKeyStatRequest;
}) {
  return (
    <PromiseContainer promise={statRequest.promise}>
      {(productStats) => {
        const palette = createPalette();
        const data = {
          datasets: productStats.map(({ productId, stats }, i) => ({
            backgroundColor: palette.getLightColor(productId, i),
            borderColor: palette.getColor(productId, i),
            data: stats,
            label: `${getProductName(productId)} (${getKeyCount(stats)})`,
          })),
        };

        return <Bar data={data} options={options} />;
      }}
    </PromiseContainer>
  );

  function getProductName(productId: string) {
    if (productId === '') {
      return 'All products';
    }

    return (
      statRequest.products.getById(productId)?.name || `Product #${productId}`
    );
  }
});

function getKeyCount(stats: PulseKeyStatRecord[]) {
  return stats.reduce((sum, stat) => sum + stat.keyCount, 0);
}

function createPalette() {
  const palette = {
    empty: '#dddddd',
    0: '#77aadd',
    1: '#ee8866',
    2: '#eedd88',
    3: '#ffaabb',
    4: '#99ddff',
    5: '#44bb99',
    6: '#bbcc33',
    7: '#aaaa00',
  };

  const skippedIds = new Set();

  function getColor(productId: string, position: number) {
    if (!productId) {
      skippedIds.add(productId);
      return palette.empty;
    }

    return (palette as Record<string, string>)[
      (position - skippedIds.size).toString(10)
    ];
  }

  return {
    getColor,

    getLightColor(productId: string, position: number) {
      const color = getColor(productId, position);
      return `${color}7f`;
    },
  };
}
